// ChatbotInterface.jsx
import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contexts/AuthContext';
import { ChatContext } from '../../contexts/ChatContext';
import { UIContext } from '../../contexts/UIContext';
import { Link as RouterLink } from 'react-router-dom';
import {
    TextField, Button, Paper, Typography, Box, CircularProgress, List, ListItem, Avatar, IconButton, Slide, Tooltip, Fab, Fade
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LoginIcon from '@mui/icons-material/Login';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReactMarkdown from 'react-markdown';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import DescriptionIcon from '@mui/icons-material/Description';
import InsightsIcon from '@mui/icons-material/Insights';
import SecurityIcon from '@mui/icons-material/Security';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ApiIcon from '@mui/icons-material/Api';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { LANGUAGES, LanguageMenuItemContent } from '../LanguageSwitcher/LanguageSwitcher';
import '../LanguageSwitcher/LanguageSwitcher.css';
import remarkGfm from 'remark-gfm';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactDOM from 'react-dom';
import './ChatbotInterface.css';

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

const loginPromptStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    height: '100%',
    padding: '2rem',
    gap: '1.5rem'
};

// Helper to fix common LLM formatting issues
const sanitizeMessageText = (text) => {
    if (!text) return "";

    let cleanText = text;

    // 1. Fix spaces in citation URLs (Original Fix)
    cleanText = cleanText.replace(/\[Source\]\(([^)]+)\)/g, (match, urlContent) => {
        return `[Source](${urlContent.replace(/\s/g, '')})`;
    });

    // 2. Ensure [Source] is on a new line if it follows a code block
    // Look for ``` followed immediately by [Source]
    cleanText = cleanText.replace(/```\s*(\[Source\])/g, '```\n\n$1');

    return cleanText;
};

const CodeBlock = ({ language, children, style }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(String(children));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div style={{ position: 'relative', margin: '10px 0' }}>
            {/* Copy Button */}
            <Tooltip title={copied ? "Copied!" : "Copy code"} placement="left">
                <IconButton
                    onClick={handleCopy}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        padding: '4px',
                        zIndex: 1, // Ensure it's above the code
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white'
                        }
                    }}
                >
                    {copied ? <CheckIcon fontSize="small" sx={{ fontSize: '14px' }} /> : <ContentCopyIcon fontSize="small" sx={{ fontSize: '14px' }} />}
                </IconButton>
            </Tooltip>

            {/* Syntax Highlighter */}
            <SyntaxHighlighter
                style={style}
                language={language}
                PreTag="div"
                customStyle={{
                    margin: 0, // Remove default margins
                    borderRadius: '8px',
                    fontSize: '0.85em',
                    padding: '1em',
                    backgroundColor: '#1e1e1e', // Ensure consistent dark background
                }}
            >
                {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
        </div>
    );
};

const ChatbotInterface = ({ isOpen, onClose }) => {
    const { t, i18n } = useTranslation();
    const { isAuthenticated, fetchWithAuth } = useContext(AuthContext);
    const { user } = useContext(AuthContext);
    const { chatMessages, setChatMessages, chatbotLanguage, setChatbotLanguage } = useContext(ChatContext);
    const chatT = i18n.getFixedT(chatbotLanguage);
    const { openDocViewer } = useContext(UIContext);

    const [input, setInput] = useState('');
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);
    const [alertDialog, setAlertDialog] = useState({ isOpen: false, title: '', message: '' });
    const closeAlertDialog = () => setAlertDialog({ ...alertDialog, isOpen: false });
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);
    const [isDragInvalid, setIsDragInvalid] = useState(false); 
    const [isBotLoading, setIsBotLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(chatT('chatbot.loadingMessage.thinking'));
    const [isMaximized, setIsMaximized] = useState(false);
    const showSuggestions = chatMessages.length === 0;
    const [needsInputFocus, setNeedsInputFocus] = useState(false);
    const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);
    const [feedback, setFeedback] = useState({});
    const [thankYouTooltipId, setThankYouTooltipId] = useState(null);
    const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
    const [langMenuAnchorEl, setLangMenuAnchorEl] = useState(null);
    const [showSources, setShowSources] = useState(true);
    const messagesEndRef = useRef(null);
    const messageItemRefs = useRef({});
    const inputRef = useRef(null);
    const chatContentRef = useRef(null);
    const scrollThrottleTimeoutRef = useRef(null);
    const isScrollingViaClickRef = useRef(false);
    const scrollCheckIntervalRef = useRef(null);
    const latestViewZoneRef = useRef({ type: null, scrollTopWhenTargeted: 0, isZoneActive: false });
    const abortControllerRef = useRef(null);
    const prevChatMessages = usePrevious(chatMessages);
    const isMoreMenuOpen = Boolean(moreMenuAnchorEl);
    
    const handleMoreMenuOpen = (event) => setMoreMenuAnchorEl(event.currentTarget);
    const handleMoreMenuClose = () => setMoreMenuAnchorEl(null);
    const handleClearChat = () => {
        setChatMessages([]);
        setAttachments([]);
        setInput(''); 
    };

    useEffect(() => {
        // This effect runs when the chat messages array changes.
        // We check if the previous state had messages and the current one is empty.
        if (prevChatMessages && prevChatMessages.length > 0 && chatMessages.length === 0) {
            console.log('Chat was cleared. Aborting any in-flight requests.');
            abortControllerRef.current?.abort();
        }
    }, [chatMessages, prevChatMessages]);

    useEffect(() => {
        const setScrollbarWidthProperty = () => {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
        };
        if (isOpen) {
            setScrollbarWidthProperty();
            window.addEventListener('resize', setScrollbarWidthProperty);
            return () => window.removeEventListener('resize', setScrollbarWidthProperty);
        }
    }, [isOpen]);

    const handleChatScroll = useCallback(() => {
        const chatContent = chatContentRef.current;
        // If the chat isn't open or the welcome screen is visible (no messages), hide the button.
        if (!chatContent || !isOpen || chatMessages.length === 0) {
            setShowScrollToBottomButton(false);
            latestViewZoneRef.current.isZoneActive = false;
            return;
        }

        if (isScrollingViaClickRef.current) {
            setShowScrollToBottomButton(false);
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = chatContent;
        const isAtAbsoluteBottom = scrollHeight - scrollTop <= clientHeight + 10;

        if (isAtAbsoluteBottom) {
            setShowScrollToBottomButton(false);
            latestViewZoneRef.current = { type: 'absoluteEnd', scrollTopWhenTargeted: scrollTop, isZoneActive: true };
            return;
        }

        if (latestViewZoneRef.current.isZoneActive) {
            const { type, scrollTopWhenTargeted } = latestViewZoneRef.current;
            const upwardScrollToDeactivateUserZone = 50;

            if (type === 'userMessageStart') {
                if (scrollTop < scrollTopWhenTargeted - upwardScrollToDeactivateUserZone) {
                    latestViewZoneRef.current.isZoneActive = false;
                } else {
                    setShowScrollToBottomButton(false);
                    return;
                }
            } else if (type === 'absoluteEnd') {
                const lastUserMessage = chatMessages.slice().reverse().find(m => m.sender === 'user');
                if (lastUserMessage) {
                    const lastUserMessageElement = messageItemRefs.current[lastUserMessage.id];
                    if (lastUserMessageElement) {
                        const userMsgTopInContent = lastUserMessageElement.offsetTop - chatContent.offsetTop;
                        if (scrollTop < userMsgTopInContent - upwardScrollToDeactivateUserZone) {
                            latestViewZoneRef.current.isZoneActive = false;
                        } else {
                            setShowScrollToBottomButton(false);
                            return;
                        }
                    } else {
                        latestViewZoneRef.current.isZoneActive = false;
                    }
                } else {
                    latestViewZoneRef.current.isZoneActive = false;
                }
            }
        }

        if (!latestViewZoneRef.current.isZoneActive) {
            const lastUserMessage = chatMessages.slice().reverse().find(m => m.sender === 'user');
            if (lastUserMessage) {
                const lastUserMessageElement = messageItemRefs.current[lastUserMessage.id];
                if (lastUserMessageElement) {
                    const userMsgTopInContent = lastUserMessageElement.offsetTop - chatContent.offsetTop;
                    const isUserMsgNearTop = scrollTop >= userMsgTopInContent - 20 && scrollTop <= userMsgTopInContent + clientHeight * 0.3;
                    const isBotResponseLikelyBelow = (scrollHeight - (userMsgTopInContent + lastUserMessageElement.offsetHeight)) > clientHeight * 0.5;

                    if (isUserMsgNearTop && isBotResponseLikelyBelow) {
                        setShowScrollToBottomButton(false);
                        latestViewZoneRef.current = { type: 'userMessageStart', scrollTopWhenTargeted: userMsgTopInContent, isZoneActive: true };
                        return;
                    }
                }
            }
        }

        const pixelsFromBottom = scrollHeight - clientHeight - scrollTop;
        const showThreshold = Math.max(70, clientHeight * 0.20);

        setShowScrollToBottomButton(pixelsFromBottom > showThreshold);
    }, [isOpen, chatMessages]);

    const commonScrollEndLogic = useCallback((finalScrollTop, targetType) => {
        isScrollingViaClickRef.current = false;
        if (scrollCheckIntervalRef.current) clearInterval(scrollCheckIntervalRef.current);
        scrollCheckIntervalRef.current = null;

        latestViewZoneRef.current = {
            type: targetType,
            scrollTopWhenTargeted: finalScrollTop,
            isZoneActive: true,
        };
        queueMicrotask(() => handleChatScroll());
    }, [handleChatScroll]);

    const smartScroll = useCallback(() => {
        if (!chatContentRef.current || !isOpen) return;
        const lastMessage = chatMessages[chatMessages.length - 1];
        const secondLastMessage = chatMessages.length > 1 ? chatMessages[chatMessages.length - 2] : null;

        setTimeout(() => {
            if (!chatContentRef.current || !isOpen) return;

            let targetElement = null;
            let scrollOptions = { behavior: "smooth" };
            let targetType = 'absoluteEnd';

            if (lastMessage?.sender === 'bot' && secondLastMessage?.sender === 'user') {
                targetElement = messageItemRefs.current[secondLastMessage.id];
                if (targetElement) {
                    scrollOptions.block = "start";
                    targetType = 'userMessageStart';
                }
            }

            if (!targetElement) {
                targetElement = messagesEndRef.current;
                targetType = 'absoluteEnd';
            }

            if (targetElement) {
                isScrollingViaClickRef.current = true;
                targetElement.scrollIntoView(scrollOptions);

                let lastScrollTopVal = chatContentRef.current.scrollTop;
                let scrollStallCount = 0;
                const maxStallChecks = 8;
                const scrollCheckIntervalMs = 50;

                const checkSmartScrollEnd = () => {
                    if (!chatContentRef.current || !isScrollingViaClickRef.current) {
                        commonScrollEndLogic(chatContentRef.current?.scrollTop || lastScrollTopVal, targetType);
                        return;
                    }
                    const currentScrollTop = chatContentRef.current.scrollTop;
                    if (Math.abs(currentScrollTop - lastScrollTopVal) < 3) scrollStallCount++;
                    else scrollStallCount = 0;
                    lastScrollTopVal = currentScrollTop;

                    if (scrollStallCount >= maxStallChecks) {
                        commonScrollEndLogic(currentScrollTop, targetType);
                    } else {
                        scrollCheckIntervalRef.current = setTimeout(checkSmartScrollEnd, scrollCheckIntervalMs);
                    }
                };
                if (scrollCheckIntervalRef.current) clearInterval(scrollCheckIntervalRef.current);
                scrollCheckIntervalRef.current = setTimeout(checkSmartScrollEnd, scrollCheckIntervalMs + 75);
            } else {
                handleChatScroll();
            }
        }, 50);
    }, [chatMessages, isOpen, handleChatScroll, commonScrollEndLogic]);

    useEffect(() => {
        if (isOpen) smartScroll();
    }, [chatMessages, isOpen, smartScroll]);

    useEffect(() => {
        if (isOpen && !isMaximized) {
            const timer = setTimeout(() => inputRef.current?.focus(), 350);
            return () => clearTimeout(timer);
        }
    }, [isOpen, isMaximized]);

    useEffect(() => {
        if (needsInputFocus && inputRef.current) {
            inputRef.current.focus();
            setNeedsInputFocus(false);
        }
    }, [needsInputFocus]);

    const handleLangMenuOpen = (event) => {
        setLangMenuAnchorEl(event.currentTarget);
    };
    const handleLangMenuClose = () => {
        setLangMenuAnchorEl(null);
        handleMoreMenuClose();
    };

    const handleLanguageChangeInChat = (langCode) => {
        abortControllerRef.current?.abort();
        setChatbotLanguage(langCode);
        setChatMessages([]);
        handleLangMenuClose();
    };

    const processFile = (file) => {
        if (!file) return;

        // Limit to 5MB
        if (file.size > 5 * 1024 * 1024) {
            setAlertDialog({
                isOpen: true,
                title: chatT('chatbot.errors.fileTooLargeTitle', 'File Too Large'),
                message: chatT('chatbot.errors.fileTooLargeMsg', 'Please select an image under 5MB.')
            });
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result;
            
            setAttachments((prev) => {
                // 1. DUPLICATE CHECK: Ignore if the exact same image is already attached
                if (prev.some(att => att.previewUrl === base64String)) {
                    return prev; // Silently ignore duplicate
                }

                // 2. LIMIT CHECK: Show custom popup if exceeding 3 images
                if (prev.length >= 3) {
                    setAlertDialog({
                        isOpen: true,
                        title: chatT('chatbot.errors.maxImagesTitle', 'Maximum Images Reached'),
                        message: chatT('chatbot.errors.maxImagesMsg', 'You can only attach up to 3 images per message.')
                    });
                    return prev;
                }

                const newAttachment = {
                    id: Date.now() + Math.random(), 
                    file: file,
                    previewUrl: base64String,
                    base64Data: base64String.split(',')[1],
                    mimeType: file.type
                };
                return [...prev, newAttachment];
            });
        };
        reader.readAsDataURL(file);
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        files.forEach(processFile);
        event.target.value = null; // Reset input
    };

    // Handle pasting images directly from the clipboard
    const handlePaste = (event) => {
        const items = event.clipboardData?.items;
        if (!items) return;

        let hasImage = false;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                hasImage = true;
                const file = items[i].getAsFile();
                processFile(file);
            }
        }
        // If an image was pasted, prevent the filename string from pasting into the text box
        if (hasImage) {
            event.preventDefault(); 
        }
    };

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];

    // Helper to check file types during drag
    const checkDragValidity = (e) => {
        if (e.dataTransfer.items) {
            for (let i = 0; i < e.dataTransfer.items.length; i++) {
                if (e.dataTransfer.items[i].kind === 'file') {
                    if (!validTypes.includes(e.dataTransfer.items[i].type)) {
                        return false; // Invalid file type found
                    }
                }
            }
        }
        return true;
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current += 1;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
            setIsDragInvalid(!checkDragValidity(e));
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) {
            setIsDragging(false);
            setIsDragInvalid(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = isDragInvalid ? 'none' : 'copy';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setIsDragInvalid(false);
        dragCounter.current = 0;

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files);
            let hasInvalid = false;

            files.forEach(file => {
                if (validTypes.includes(file.type)) {
                    processFile(file);
                } else {
                    hasInvalid = true;
                }
            });

            // Show popup if they dropped a PDF or unsupported file
            if (hasInvalid) {
                setAlertDialog({
                    isOpen: true,
                    title: chatT('chatbot.errors.invalidFileTypeTitle', 'Unsupported File Type'),
                    message: chatT('chatbot.errors.invalidFileTypeMsg', 'Only PNG, JPEG, and WEBP images are currently supported.')
                });
            }
        }
    };

    const handleRemoveAttachment = (idToRemove) => {
        setAttachments(prev => prev.filter(att => att.id !== idToRemove));
    };

    const handleSend = useCallback(async (textOverride) => {
        // Abort any previous, lingering request before starting a new one.
        abortControllerRef.current?.abort();
        // Create a new controller for this specific request.
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const trimmedInput = (typeof textOverride === 'string' ? textOverride : input).trim();
        if (!trimmedInput && attachments.length === 0) return;

        const timestamp = Date.now();
        // Include the attachment in the user message for UI rendering
        const userMessage = { 
            id: `user-${timestamp}`, 
            sender: 'user', 
            text: trimmedInput,
            attachments: attachments.map(att => att.previewUrl)
        };

        let newMessages = [];
        let historyForAPI = [];

        if (chatMessages.length === 0) {
            // This is the start of a new conversation. Add the greeting.
            let greetingKey = 'chatbot.initialGreetingGuest';
            let greetingParams = {};
            if (user && user.full_name && !user.email.endsWith('@guest.local')) {
                greetingKey = 'chatbot.initialGreetingUser';
                greetingParams = { userName: user.full_name };
            }
            const greetingMessage = {
                id: 'initial-bot-msg',
                sender: 'bot',
                text: chatT(greetingKey, greetingParams)
            };
            newMessages.push(greetingMessage, userMessage);
            // The history for the API should NOT include our client-side greeting.
            historyForAPI = [];
        } else {
            // This is a continuing conversation.
            newMessages = [...chatMessages, userMessage];
            historyForAPI = chatMessages.slice().map(msg => ({ sender: msg.sender, text: msg.text }));
        }

        setChatMessages(newMessages); // Update the UI immediately with the correct messages
        // --- END OF KEY LOGIC CHANGE ---

        if (typeof textOverride !== 'string') {
            setInput('');
        }

        // Capture attachment for API payload, then clear UI state
        const currentAttachments = attachments;
        setAttachments([]);

        setIsBotLoading(true);
        setLoadingMessage(chatT('chatbot.loadingMessage.initializing'));

        try {
            const requestBody = {
                question: trimmedInput,
                history: historyForAPI,
                language: chatbotLanguage,
                attachments: currentAttachments.map(att => ({
                    mime_type: att.mimeType,
                    data: att.base64Data
                }))
            };
            const response = await fetchWithAuth('/api/chatbot_rag', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
                signal: controller.signal
            });

            if (response.status === 429) throw new Error(chatT('chatbot.rateLimitMessage'));
            if (!response.ok || !response.body) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || `An unexpected error occurred (Status: ${response.status})`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let finalStreamEnded = false;

            while (!finalStreamEnded) {
                const { value, done } = await reader.read();
                if (done) {
                    finalStreamEnded = true;
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const sseMessages = chunk.split('\n\n');

                for (const messageString of sseMessages) {
                    if (messageString.startsWith("data:")) {
                        const jsonData = messageString.substring(5).trim();
                        if (jsonData) {
                            try {
                                const eventData = JSON.parse(jsonData);

                                if (eventData.messageKey) setLoadingMessage(t(eventData.messageKey));

                                if (eventData.status === "completed") {
                                    const botResponseMessage = { id: `bot-${Date.now()}`, sender: 'bot', text: eventData.answer, metadata: eventData.metadata };
                                    setChatMessages(prev => [...prev, botResponseMessage]);
                                    finalStreamEnded = true;
                                } else if (eventData.status === "error") {
                                    throw new Error(eventData.message || 'An error occurred during analysis.');
                                } else if (eventData.status === "stream_ended") {
                                    finalStreamEnded = true;
                                }
                            } catch (e) { console.error("Error parsing SSE JSON:", e, jsonData); }
                        }
                    }
                }
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Fetch aborted'); // Log for debugging, but don't show an error to the user
                setIsBotLoading(false); // Make sure to reset loading state
                return; // Stop execution
            }
            console.error('Chatbot API error:', error);
            const errorMessageText = error.message || 'An unexpected error occurred.';
            const networkErrorMessage = { id: `bot-error-${Date.now()}`, sender: 'bot', text: chatT('chatbot.errorMessage', { message: errorMessageText }) };
            setChatMessages(prevMessages => [...prevMessages, networkErrorMessage]);
        } finally {
            setIsBotLoading(false);
            setNeedsInputFocus(true);
            // Clean up the controller ref if this request finished (or failed) without being aborted
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
        }
    }, [input, attachments, chatMessages, user, t, setChatMessages, fetchWithAuth, chatbotLanguage, chatT]);

    const handleFeedback = async (message, rating) => {
        const messageId = message.id;
        if (feedback[messageId]) return;

        setFeedback(prev => ({ ...prev, [messageId]: rating }));

        try {
            const messageIndex = chatMessages.findIndex(m => m.id === messageId);
            let originalQuestion = '';
            for (let i = messageIndex - 1; i >= 0; i--) {
                if (chatMessages[i].sender === 'user') {
                    originalQuestion = chatMessages[i].text;
                    break;
                }
            }

            await fetchWithAuth('/api/chatbot_rag/chatbot_rag_feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messageId, rating, originalQuestion, finalAnswer: message.text, metadata: message.metadata || {}, language: i18n.language })
            });

            setFeedback(prev => ({ ...prev, [messageId]: 'sent' }));
            setThankYouTooltipId(messageId);
            setTimeout(() => setThankYouTooltipId(null), 1500);
        } catch (err) {
            console.error("Failed to submit feedback:", err);
            setFeedback(prev => {
                const { [messageId]: _, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleKeyPress = useCallback((event) => {
        if (event.key === 'Enter' && !event.shiftKey && !isBotLoading) {
            event.preventDefault();
            handleSend();
        }
    }, [handleSend, isBotLoading]);

    const handleSuggestionClick = (prompt) => handleSend(prompt);

    const handleToggleMaximize = () => {
        setIsMaximized(prev => !prev);
        if (isMaximized) setNeedsInputFocus(true);
        setTimeout(() => {
            if (chatContentRef.current) {
                if (latestViewZoneRef.current.type === 'userMessageStart') {
                    const lastUserMsg = chatMessages.slice().reverse().find(m => m.sender === 'user');
                    if (lastUserMsg && messageItemRefs.current[lastUserMsg.id]) {
                        latestViewZoneRef.current.scrollTopWhenTargeted = messageItemRefs.current[lastUserMsg.id].offsetTop - chatContentRef.current.offsetTop;
                    } else { latestViewZoneRef.current.isZoneActive = false; }
                } else if (latestViewZoneRef.current.type === 'absoluteEnd') {
                    latestViewZoneRef.current.scrollTopWhenTargeted = chatContentRef.current.scrollHeight - chatContentRef.current.clientHeight;
                }
                handleChatScroll();
            }
        }, 350);
    };

    useEffect(() => {
        const chatContent = chatContentRef.current;
        const throttledScrollHandler = () => {
            if (scrollThrottleTimeoutRef.current) return;
            scrollThrottleTimeoutRef.current = setTimeout(() => {
                scrollThrottleTimeoutRef.current = null;
                if (!isScrollingViaClickRef.current) handleChatScroll();
            }, 150);
        };

        if (chatContent && isOpen) {
            chatContent.addEventListener('scroll', throttledScrollHandler);
            const initialCheckTimer = setTimeout(() => {
                if (!isScrollingViaClickRef.current && isOpen) {
                    latestViewZoneRef.current.isZoneActive = false;
                    handleChatScroll();
                }
            }, 250);
            return () => {
                clearTimeout(initialCheckTimer);
                chatContent.removeEventListener('scroll', throttledScrollHandler);
                if (scrollThrottleTimeoutRef.current) clearTimeout(scrollThrottleTimeoutRef.current);
                if (scrollCheckIntervalRef.current) clearInterval(scrollCheckIntervalRef.current);
            };
        } else if (!isOpen && chatContentRef.current) {
            setShowScrollToBottomButton(false);
            latestViewZoneRef.current.isZoneActive = false;
        }
    }, [isOpen, handleChatScroll]);

    const scrollToBottomOnClick = () => {
        if (!chatContentRef.current || !isOpen) return;
        if (scrollCheckIntervalRef.current) clearInterval(scrollCheckIntervalRef.current);

        isScrollingViaClickRef.current = true;
        setShowScrollToBottomButton(false);

        const lastMessage = chatMessages[chatMessages.length - 1];
        const secondLastMessage = chatMessages.length > 1 ? chatMessages[chatMessages.length - 2] : null;

        let targetElement = null;
        let scrollOptions = { behavior: "smooth" };
        let targetType = 'absoluteEnd';

        if (lastMessage?.sender === 'bot' && secondLastMessage?.sender === 'user') {
            targetElement = messageItemRefs.current[secondLastMessage.id];
            if (targetElement) {
                scrollOptions.block = "start";
                targetType = 'userMessageStart';
            }
        }

        if (!targetElement) {
            targetElement = messagesEndRef.current;
            targetType = 'absoluteEnd';
        }

        if (targetElement) {
            targetElement.scrollIntoView(scrollOptions);

            let lastScrollTopVal = chatContentRef.current.scrollTop;
            let scrollStallCount = 0;
            const maxStallChecks = 8;
            const scrollCheckIntervalMs = 50;

            const checkFabScrollEnd = () => {
                if (!chatContentRef.current || !isScrollingViaClickRef.current) {
                    commonScrollEndLogic(chatContentRef.current?.scrollTop || lastScrollTopVal, targetType);
                    return;
                }
                const currentScrollTop = chatContentRef.current.scrollTop;
                if (Math.abs(currentScrollTop - lastScrollTopVal) < 3) scrollStallCount++;
                else scrollStallCount = 0;
                lastScrollTopVal = currentScrollTop;

                if (scrollStallCount >= maxStallChecks) {
                    commonScrollEndLogic(currentScrollTop, targetType);
                } else {
                    scrollCheckIntervalRef.current = setTimeout(checkFabScrollEnd, scrollCheckIntervalMs);
                }
            };
            if (scrollCheckIntervalRef.current) clearInterval(scrollCheckIntervalRef.current);
            scrollCheckIntervalRef.current = setTimeout(checkFabScrollEnd, scrollCheckIntervalMs + 75);
        } else {
            isScrollingViaClickRef.current = false;
            handleChatScroll();
        }
    };

    const formatSourcePath = (path) => {
        const pathWithoutExt = path.replace(/\.md$/, '');
        const parts = pathWithoutExt.split('/');
        const formattedParts = parts.map(part =>
            part.replace(/_/g, ' ').replace(/\bai\b/gi, 'AI').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        );
        return formattedParts.join(' -> ');
    };

    const CustomLinkRenderer = ({ href, children }) => {
        if (showSources && String(children).trim() === 'Source') {
            const sources = href.split(';');
            const tooltipTitle = (
                <>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '4px' }}>
                        {sources.length > 1 ? chatT('chatbot.tooltip.multipleSources') : chatT('chatbot.tooltip.source')}:
                    </div>
                    <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                        {sources.map((source, index) => (
                            <li key={index} style={{ marginBottom: '2px', textAlign: 'left' }}>
                                {formatSourcePath(source)}
                            </li>
                        ))}
                    </ul>
                </>
            );
            return (
                <Tooltip title={tooltipTitle} placement="top" arrow>
                    <IconButton className="markdown-source-link" onClick={() => openDocViewer(sources[0])} size="small">
                        <MenuBookIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Tooltip>
            );
        }

        if (!showSources && String(children).trim() === 'Source') return null;

        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    };

    return (
        <>
            <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit timeout={300}>
                <Paper
                    className={`chat-window-container ${isMaximized ? 'maximized' : ''}`}
                    elevation={10} role="dialog" aria-modal="true" aria-label="AI Assistant Chat Window"
                >
                    <Box className="chat-header">
                        <img src="/static/chatbot_logo_vetraai_head.webp" alt="Chatbot Head" style={{ width: '24px', height: '24px', marginRight: '8px', color: 'white' }} />
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>{chatT('chatbot.headerTitle')}</Typography>

                        {isAuthenticated && (
                            <>
                                <Tooltip title={chatT('chatbot.menu.clearChat')}>
                                    <IconButton onClick={handleClearChat} size="small" className="chat-header-button">
                                        <DeleteSweepIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={isMaximized ? chatT('chatbot.unmaximizeTooltip') : chatT('chatbot.maximizeTooltip')}>
                                    <IconButton onClick={handleToggleMaximize} size="small" className="chat-header-button">
                                        {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="More Options">
                                    <IconButton onClick={handleMoreMenuOpen} size="small" className="chat-header-button">
                                        <MoreVertIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}

                        <Tooltip title={chatT('chatbot.closeTooltip')}>
                            <IconButton onClick={onClose} size="small" className="chat-header-button">
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Menu
                        anchorEl={moreMenuAnchorEl}
                        open={isMoreMenuOpen}
                        onClose={handleMoreMenuClose}
                        PaperProps={{ sx: { mt: 1, minWidth: 260 } }}
                        MenuListProps={{ sx: { paddingTop: 0 } }}
                        sx={{ zIndex: 1355 }}
                    >
                        {/* This item doesn't have a tooltip key, so we'll add a descriptive title */}
                        <Tooltip title="Change chat language" placement="left" arrow>
                            <MenuItem onClick={handleLangMenuOpen}>
                                <LanguageIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                                <ListItemText primary={chatT('chatbot.menu.language')} />
                                <ArrowRightIcon />
                            </MenuItem>
                        </Tooltip>

                        <Tooltip title={showSources ? chatT('chatbot.tooltip.disableSources') : chatT('chatbot.tooltip.enableSources')} placement="left" arrow>
                            <MenuItem onClick={() => setShowSources(prev => !prev)}>
                                <Box sx={{ mr: 1.5, color: 'text.secondary', position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <MenuBookIcon />
                                    {showSources ? (
                                        <CheckCircleIcon sx={{ position: 'absolute', top: '-5px', right: '-7px', fontSize: '1rem', color: 'success.main', backgroundColor: 'white', borderRadius: '50%' }} />
                                    ) : (
                                        <CancelIcon sx={{ position: 'absolute', top: '-5px', right: '-7px', fontSize: '1rem', color: 'error.main', backgroundColor: 'white', borderRadius: '50%' }} />
                                    )}
                                </Box>
                                <ListItemText primary={showSources ? chatT('chatbot.menu.hideSources') : chatT('chatbot.menu.showSources')} />
                            </MenuItem>
                        </Tooltip>

                        <Tooltip title={chatT('chatbot.tooltip.browseDocs')} placement="left" arrow>
                            <MenuItem onClick={() => { openDocViewer(); handleMoreMenuClose(); }}>
                                <LibraryBooksIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                                <ListItemText primary={chatT('chatbot.menu.exploreDocs')} />
                            </MenuItem>
                        </Tooltip>
                    </Menu>

                    <Menu
                        anchorEl={langMenuAnchorEl}
                        open={Boolean(langMenuAnchorEl)}
                        onClose={handleLangMenuClose}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ 'aria-labelledby': 'language-button' }}
                        className="language-menu-item"
                        PaperProps={{
                            className: 'language-menu-paper',
                            sx: { ml: '-1px !important', mt: '-2px !important' } // Add this negative margin to close the gap
                        }}
                        sx={{ zIndex: 1355 }}
                    >
                        {LANGUAGES.map((lang) => (
                            <MenuItem
                                key={lang.code}
                                selected={chatbotLanguage.startsWith(lang.code)}
                                onClick={() => handleLanguageChangeInChat(lang.code)}
                                className="language-menu-item" // Use the class from the CSS file
                            >
                                {/* Use the new reusable component */}
                                <LanguageMenuItemContent
                                    lang={lang}
                                    isSelected={chatbotLanguage.startsWith(lang.code)}
                                />
                            </MenuItem>
                        ))}
                    </Menu>

                    {!isAuthenticated ? (
                        <Box sx={loginPromptStyles}>
                            <img src="/static/chatbot_logo_vetraai.webp" alt="Chatbot" style={{ width: '64px', height: '64px' }} />
                            <Typography variant="h6" component="h2" color="text.primary">
                                {chatT('chatbot.loginPrompt.title')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {chatT('chatbot.loginPrompt.description')}
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/login"
                                variant="contained"
                                startIcon={<LoginIcon />}
                                sx={{ backgroundColor: 'var(--secondary-color)', '&:hover': { backgroundColor: 'var(--primary-color)' } }}
                            >
                                {chatT('chatbot.loginPrompt.button')}
                            </Button>
                        </Box>
                    ) : (
                        <>
                        <Box 
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                flexGrow: 1, 
                                position: 'relative',
                                overflow: 'hidden' // Keeps the overlay contained
                            }}
                        >
                            {/* UPDATED: Visual Overlay when dragging over */}
                            {isDragging && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: isDragInvalid ? 'rgba(253, 237, 237, 0.95)' : 'rgba(245, 249, 255, 0.95)',
                                        zIndex: 1300, // Covers messages, scrollbar, and FAB
                                        display: 'flex',
                                        flexDirection: 'row', // Keeps icon and text on the same line
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 2, 
                                        color: isDragInvalid ? '#d32f2f' : 'var(--secondary-color)',
                                    }}
                                >
                                    {isDragInvalid ? <CancelIcon sx={{ fontSize: 36 }} /> : <AttachFileIcon sx={{ fontSize: 36 }} />}
                                    <Typography variant="h6" fontWeight="500">
                                        {isDragInvalid 
                                            ? chatT('chatbot.dropInvalid', 'Unsupported file type') 
                                            : chatT('chatbot.dropValid', 'Drop image to attach')}
                                    </Typography>
                                </Box>
                            )}
                                <Box className="chat-content" ref={chatContentRef} aria-live="polite" aria-atomic="false">
                                    {showSuggestions ? (
                                        // We remove all flex centering and minHeight.
                                        // We add a large top padding (pt) to push the content down.
                                        // This ensures the top is always reachable by scrolling up.
                                        <Box sx={{
                                            textAlign: 'center',
                                            p: { xs: 2, sm: 3 },
                                            pt: { xs: 0.5, sm: 1 } // Add significant top padding
                                        }}>
                                            <Box component="img" src="/static/chatbot_logo_vetraai.webp" alt="Chatbot" sx={{ width: '64px', height: '64px', mb: 1, mx: 'auto' }} />
                                            <Typography variant="h6">
                                                {user && user.full_name && !user.email.endsWith('@guest.local')
                                                    ? chatT('chatbot.welcome.title', { userName: user.full_name })
                                                    : chatT('chatbot.welcome.titleGuest')
                                                }
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: isMaximized ? '500px' : '350px', mx: 'auto', transition: 'max-width 0.3s ease-in-out' }}>
                                                {chatT('chatbot.welcome.description')}
                                            </Typography>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                                                {chatT('chatbot.welcome.promptHeader')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: isMaximized ? '550px' : '400px', mx: 'auto', transition: 'max-width 0.3s ease-in-out' }}>
                                                <Button variant="outlined" size="small" startIcon={<FindInPageIcon />} onClick={() => handleSuggestionClick(chatT('chatbot.suggestedPrompts.prompt1'))} sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderRadius: '8px' }}>
                                                    {chatT('chatbot.suggestedPrompts.prompt1')}
                                                </Button>
                                                <Button variant="outlined" size="small" startIcon={<DescriptionIcon />} onClick={() => handleSuggestionClick(chatT('chatbot.suggestedPrompts.prompt2'))} sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderRadius: '8px' }}>
                                                    {chatT('chatbot.suggestedPrompts.prompt2')}
                                                </Button>
                                                <Button variant="outlined" size="small" startIcon={<InsightsIcon />} onClick={() => handleSuggestionClick(chatT('chatbot.suggestedPrompts.prompt3'))} sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderRadius: '8px' }}>
                                                    {chatT('chatbot.suggestedPrompts.prompt3')}
                                                </Button>
                                                <Button variant="outlined" size="small" startIcon={<SecurityIcon />} onClick={() => handleSuggestionClick(chatT('chatbot.suggestedPrompts.prompt4'))} sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderRadius: '8px' }}>
                                                    {chatT('chatbot.suggestedPrompts.prompt4')}
                                                </Button>
                                                <Button variant="outlined" size="small" startIcon={<GpsFixedIcon />} onClick={() => handleSuggestionClick(chatT('chatbot.suggestedPrompts.prompt5'))} sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderRadius: '8px' }}>
                                                    {chatT('chatbot.suggestedPrompts.prompt5')}
                                                </Button>
                                                <Button variant="outlined" size="small" startIcon={<ApiIcon />} onClick={() => handleSuggestionClick(chatT('chatbot.suggestedPrompts.prompt6'))} sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderRadius: '8px' }}>
                                                    {chatT('chatbot.suggestedPrompts.prompt6')}
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <List disablePadding>
                                            {/* The message list remains completely unchanged */}
                                            {chatMessages.map((msg, index) => {
                                                const canHaveFeedback = msg.sender === 'bot' && msg.id.startsWith('bot-') && index > 0;
                                                return (
                                                    <ListItem key={msg.id} className={`chat-message-item ${msg.sender === 'user' ? 'user' : 'bot'}`} ref={el => messageItemRefs.current[msg.id] = el}>
                                                        <Box className={`chat-message-bubble-wrapper ${msg.sender === 'user' ? 'user' : 'bot'}`}>
                                                            <Avatar
                                                                src={msg.sender === 'bot' ? "/static/chatbot_logo_vetraai_head.webp" : undefined}
                                                                className={`chat-avatar ${msg.sender === 'bot' ? 'bot' : 'user'}`}
                                                            >
                                                                {msg.sender === 'user' && <PersonIcon fontSize="small" />}
                                                            </Avatar>
                                                            <Paper elevation={1} className={`chat-message-bubble ${msg.sender === 'user' ? 'user' : 'bot'} ${canHaveFeedback ? 'has-feedback' : ''}`}>
                                                                <Box className="markdown-content" sx={(theme) => ({ ...theme.typography.body2, color: 'inherit', paddingRight: canHaveFeedback ? '0px' : 0, '& p': { marginTop: theme.spacing(0.5), marginBottom: theme.spacing(0.5), '&:first-of-type': { marginTop: 0 }, '&:last-of-type': { marginBottom: 0 }, }, '& ol, & ul': { marginTop: theme.spacing(1), marginBottom: theme.spacing(1), paddingLeft: theme.spacing(3), }, '& li': { marginBottom: theme.spacing(0.5) }, '& strong': { fontWeight: 'bold' }, })}>
                                                                    {/* Render user uploaded image */}
                                                                    {msg.attachments && msg.attachments.length > 0 && (
                                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: msg.text ? 1 : 0 }}>
                                                                            {msg.attachments.map((url, i) => (
                                                                                <Box key={i} component="img" src={url} alt={`Attachment ${i}`} className="chat-message-attachment" />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                    <ReactMarkdown 
                                                                        remarkPlugins={[remarkGfm]} 
                                                                        components={{ 
                                                                            a: CustomLinkRenderer,
                                                                            // --- Custom Code Block Renderer ---
                                                                            code({node, inline, className, children, ...props}) {
                                                                                const match = /language-(\w+)/.exec(className || '');
                                                                                return !inline && match ? (
                                                                                    <CodeBlock
                                                                                        style={vscDarkPlus}
                                                                                        language={match[1]}
                                                                                    >
                                                                                        {children}
                                                                                    </CodeBlock>
                                                                                ) : (
                                                                                    <code className={className} {...props}>
                                                                                        {children}
                                                                                    </code>
                                                                                )
                                                                            }
                                                                        }}
                                                                    >
                                                                        {/* Keep your existing sanitizer logic */}
                                                                        {/* {msg.text.replace(/\[Source\]\(([^)]+)\)/g, (m, url) => `[Source](${url.replace(/\s/g, '')})`)} */}
                                                                        {sanitizeMessageText(msg.text)}
                                                                    </ReactMarkdown>
                                                                </Box>
                                                                {canHaveFeedback && (
                                                                    <Box className="feedback-container">
                                                                        {feedback[msg.id] === 'sent' ? (
                                                                            <Tooltip open={thankYouTooltipId === msg.id} onClose={() => { }} onOpen={() => { }} title={chatT('chatbot.feedback.thanks')} placement="top" arrow>
                                                                                <CheckCircleIcon sx={{ color: 'success.light', fontSize: '1.2rem', padding: '4px' }} />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <>
                                                                                <Tooltip title={chatT('chatbot.feedback.good')}><IconButton size="small" onClick={() => handleFeedback(msg, 'good')} disabled={!!feedback[msg.id]}>{feedback[msg.id] === 'good' ? <ThumbUpIcon sx={{ fontSize: '1.1rem', color: 'success.main' }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: '1.1rem', color: 'success.main', opacity: 0.6 }} />}</IconButton></Tooltip>
                                                                                <Tooltip title={chatT('chatbot.feedback.bad')}><IconButton size="small" onClick={() => handleFeedback(msg, 'bad')} disabled={!!feedback[msg.id]}>{feedback[msg.id] === 'bad' ? <ThumbDownIcon sx={{ fontSize: '1.1rem', color: 'error.main' }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: '1.1rem', color: 'error.main', opacity: 0.6 }} />}</IconButton></Tooltip>
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Paper>
                                                        </Box>
                                                    </ListItem>
                                                );
                                            })}
                                            {isBotLoading && (
                                                <ListItem className="chat-message-item bot">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', paddingTop: '6px' }}>
                                                        <Avatar src="/static/chatbot_logo_vetraai_head.webp" className="chat-avatar bot" />
                                                        <CircularProgress size={20} sx={{ color: '#171f29' }} />
                                                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>{loadingMessage}</Typography>
                                                    </Box>
                                                </ListItem>
                                            )}
                                            <div ref={messagesEndRef} style={{ height: '1px' }} />
                                        </List>
                                    )}
                                </Box>

                                <Fade in={showScrollToBottomButton} timeout={300} unmountOnExit>
                                    <Tooltip title={chatT('chatbot.scrollToBottomTooltip')} placement="top">
                                        <Fab
                                            color="primary"
                                            size="small"
                                            aria-label="scroll to bottom"
                                            onClick={scrollToBottomOnClick}
                                            sx={{ position: 'absolute', bottom: '85px', right: '27px', zIndex: 10, backgroundColor: 'var(--primary-color)', color: 'var(--text-primary-color-white)', '&:hover': { backgroundColor: 'var(--secondary-color)' }, '.MuiTouchRipple-child': { backgroundColor: 'rgba(255, 255, 255, 0.3)' } }}
                                        >
                                            <ArrowDownwardIcon />
                                        </Fab>
                                    </Tooltip>
                                </Fade>


                                {attachments.length > 0 && (
                                    <Box className="attachment-preview-container">
                                        {attachments.map(att => (
                                            <Box key={att.id} className="attachment-thumbnail-wrapper">
                                                <img src={att.previewUrl} alt="Preview" className="attachment-thumbnail" />
                                                <IconButton size="small" className="attachment-remove-btn" onClick={() => handleRemoveAttachment(att.id)}>
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <Box className="chat-input-area" component="form" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/png, image/jpeg, image/webp"
                                            style={{ display: 'none' }}
                                            ref={fileInputRef}
                                            onChange={handleFileSelect}
                                        />
                                        <Tooltip 
                                            arrow
                                            placement="bottom"
                                            title={
                                                attachments.length >= 3 
                                                    ? chatT('chatbot.tooltip.maxFiles', "Max 3 files are allowed. Please remove one to upload another one.") 
                                                    : chatT('chatbot.tooltip.attachFile', "Attach a file or paste a screenshot (max 5MB each).")
                                            }
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <IconButton 
                                                    color="primary" 
                                                    onClick={() => fileInputRef.current.click()}
                                                    disabled={attachments.length >= 3}
                                                    sx={{ 
                                                        mr: 1, 
                                                        // Normal color when under 3 images:
                                                        color: attachments.length >= 3 ? '#d32f2f' : 'var(--text-secondary-color)',
                                                        // Overrides MUI's default grey disabled color to RED when at 3 images:
                                                        '&.Mui-disabled': {
                                                            color: attachments.length >= 3 ? '#d32f2f' : 'rgba(0, 0, 0, 0.26)',
                                                            opacity: 0.85
                                                        }
                                                    }}
                                                >
                                                    <Badge 
                                                        badgeContent={attachments.length} 
                                                        color={attachments.length >= 3 ? "error" : "secondary"}
                                                    >
                                                        <AttachFileIcon />
                                                    </Badge>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <TextField
                                            inputRef={inputRef}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder={chatT('chatbot.inputPlaceholder')}
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            onPaste={handlePaste}
                                            multiline
                                            maxRows={isMaximized ? 8 : 4}
                                            sx={{ mr: 1, flexGrow: 1 }}
                                            aria-label={chatT('chatbot.inputPlaceholder')}
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={isBotLoading || (!input.trim() && attachments.length === 0)}
                                            className="chat-send-action-button"
                                            sx={{ p: '8px' }}
                                            aria-label={chatT('chatbot.sendAriaLabel')}
                                        >
                                            {isBotLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}
                </Paper>
            </Slide>
            {ReactDOM.createPortal(
                <div className={`chatbot-alert-overlay ${alertDialog.isOpen ? 'open' : ''}`} onClick={closeAlertDialog}>
                    {alertDialog.isOpen && (
                        <div className="chatbot-alert-content" onClick={(e) => e.stopPropagation()}>
                            <IconButton aria-label="close" onClick={closeAlertDialog} className="chatbot-alert-close">
                                <CloseIcon />
                            </IconButton>
                            <h2 className="chatbot-alert-title">{alertDialog.title}</h2>
                            <div className="chatbot-alert-text">
                                <p>{alertDialog.message}</p>
                            </div>
                            <div className="chatbot-alert-actions">
                                <Button 
                                    onClick={closeAlertDialog} 
                                    variant="contained" 
                                    color="primary" 
                                    sx={{ backgroundColor: 'var(--secondary-color)', '&:hover': { backgroundColor: 'var(--primary-color)'} }}
                                >
                                    {chatT('chatbot.general.ok', 'OK')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
};

export default ChatbotInterface